// Your subscription (commercial packaging Phase 3 — tenant UX = READ + REQUEST).
// Replaces the old TenantFeatureManager shell, which read real features but
// "saved" to localStorage while claiming changes applied — an honest surface
// now: the plan you're on, what it includes, your add-ons, and how to request
// more. Entitlement changes are made by KaziPeople through the audited
// commercial lifecycle; nothing here writes.
import React, { useEffect, useState } from 'react';
import { BadgeCheck, Boxes, Mail, Sparkles } from 'lucide-react';
import { Card, CardContent, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { getMyTenantFeatures } from '@/lib/hrApi';
import { getSummary, type BillingSummary } from '@/services/billingApi';

const PRODUCT_LABELS: Record<string, string> = {
  core_hr: 'Core HR', payroll: 'Payroll', leave: 'Leave', benefits: 'Benefits',
  credit: 'Loans & Advances', performance: 'Performance', workforce_framework: 'Workforce Frameworks',
  expenses: 'Expense Management', work: 'Work Management', learning: 'Learning & Development',
  statutory_submissions: 'Statutory Submissions', analytics: 'Advanced Analytics',
  dana_ai: 'Dana AI Assistant', dana_voice: 'Dana Voice', dana_localization: 'Dana Localization',
  dana_managed_key: 'Managed AI Keys', compliance: 'Compliance', payments: 'Payments Gateway',
  developer_platform: 'Developer Platform', commission: 'Commissions', integrations: 'Integrations',
  performance_advanced: 'Advanced Performance', rbac_custom_roles: 'Custom Roles',
};

export const FeatureManagementPage: React.FC = () => {
  const [features, setFeatures] = useState<Array<{ featureCode: string; isEnabled: boolean }>>([]);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.allSettled([getMyTenantFeatures(), getSummary()]).then(([f, s]) => {
      if (f.status === 'fulfilled') setFeatures(((f.value as any)?.features ?? []).filter((x: any) => x.isEnabled));
      if (s.status === 'fulfilled') setSummary(s.value as any);
      setLoaded(true);
    });
  }, []);

  const label = (code: string) => PRODUCT_LABELS[code] || code.replace(/_/g, ' ');
  const plan = (summary as any)?.plan;
  const addOns: Array<{ productCode: string }> = (summary as any)?.addOns ?? [];
  const shown = new Set<string>();
  const productRows = features.filter((f) => {
    const key = label(f.featureCode);
    if (shown.has(key)) return false; // collapse aliases to one row
    shown.add(key);
    return true;
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Sparkles className="w-6 h-6 text-orange-500" />Your subscription</h1>
        <p className="text-sm text-muted-foreground mt-1">What your KaziPeople workspace includes. Changes to your plan are made with KaziPeople — contact us to add products or raise limits.</p>
      </div>

      <Card><CardContent className="pt-4 space-y-2">
        <CardTitle className="text-base flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-orange-500" />{plan ? `${plan.name}${plan.version ? ` (v${plan.version})` : ''}` : 'Your plan'}</CardTitle>
        {plan?.description && <p className="text-sm text-muted-foreground">{plan.description}</p>}
        {!plan && loaded && <p className="text-sm text-muted-foreground">Your subscription details are managed by KaziPeople. The products below are what your workspace currently includes.</p>}
        {addOns.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {addOns.map((a) => <Badge key={a.productCode} className="text-[10px]">{label(a.productCode)} (add-on)</Badge>)}
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="pt-4 space-y-2.5">
        <CardTitle className="text-base flex items-center gap-2"><Boxes className="w-4 h-4 text-orange-500" />Included products</CardTitle>
        {!loaded && <p className="text-sm text-muted-foreground">Loading…</p>}
        {loaded && productRows.length === 0 && <p className="text-sm text-muted-foreground">No product data yet — your workspace runs on the platform defaults.</p>}
        <div className="grid sm:grid-cols-2 gap-2">
          {productRows.map((f) => (
            <div key={f.featureCode} className="rounded-md border border-border px-3 py-2 text-sm flex items-center justify-between">
              <span>{label(f.featureCode)}</span>
              <Badge variant="outline" className="text-[10px]">Included</Badge>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card><CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">Need more?</CardTitle>
          <p className="text-sm text-muted-foreground">Add-ons (Dana Voice, Advanced Analytics, extra integrations…), higher limits, or a plan change.</p>
        </div>
        <Button variant="outline" asChild>
          <a href="mailto:sales@kazipeople.com?subject=Subscription%20change%20request"><Mail className="mr-1 h-4 w-4" />Contact KaziPeople</a>
        </Button>
      </CardContent></Card>
    </div>
  );
};

export default FeatureManagementPage;
